"use client"
import React, { createContext, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import useWebSocket from '@/hooks/useWebSocket';
import { useCookie } from '@/hooks/useCookie';


type WebSocketSceneType = 'paper_trans' | 'payment' | 'image_gen';
interface WebSocketDataType {
  scene: WebSocketSceneType,
  event: string;
  id: number;
  [key: string]: any;
}
type WebSocketCallbackType =
  ((data: WebSocketDataType, event: MessageEvent) => void)
  & { scene?: string }

/**
 * 定义 WebSocket Context 的类型。
 * 包含连接状态、发送消息函数和添加消息监听器函数。
 */
interface WebSocketContextType {
  isConnected: boolean; // WebSocket 连接是否已建立
  sendMessage: (message: string | object) => void; // 用于向 WebSocket 发送消息的函数
  // 用于订阅 WebSocket 消息的方法
  addMessageListener: (
    callback: WebSocketCallbackType,
    scene?: string,
  ) => () => void;
}

const host = process.env.NEXT_PUBLIC_API_HOST;

// 创建 WebSocket Context，初始值为 undefined
const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

/**
 * WebSocketProvider 的 Props 接口。
 */
interface WebSocketProviderProps {
  children: ReactNode; // React 子节点，将被 Provider 包裹
}

/**
 * WebSocketProvider 组件，用于在整个应用中提供 WebSocket 连接。
 * 它在内部使用 useWebSocket hook 来管理连接，并提供一个上下文，
 * 使得所有子组件都可以访问 WebSocket 的状态和功能。
 */
export const WebSocketProvider = ({
  children,
}: WebSocketProviderProps) => {
  const SESSID = useCookie('FDSID');
  // 使用 useRef 存储消息监听器数组，避免在组件重新渲染时重复创建或丢失监听器
  const messageListeners = useRef<Array<WebSocketCallbackType>>([]);
  /**
   * 添加消息监听器的函数。
   * 当 WebSocket 接收到新消息时，所有注册的监听器都会被调用。
   * 返回一个清理函数，用于移除当前监听器。
   */
  const addMessageListener = useCallback((
    callback: WebSocketCallbackType,
    scene: string = '',
  ) => {
    callback.scene = scene;
    messageListeners.current.push(callback); // 将回调函数添加到监听器数组
    return () => { // 返回一个清理函数
      // 从监听器数组中移除当前回调函数
      messageListeners.current = messageListeners.current.filter(listener => listener !== callback);
    };
  }, []);

  /**
   * 处理全局 WebSocket 消息的回调函数。
   * 尝试将消息解析为 JSON，然后分发给所有注册的监听器。
   * 如果解析失败，则直接传递原始数据。
   */
  const handleGlobalMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data); // 尝试将消息数据解析为 JSON
      // 这里可以根据消息类型添加过滤逻辑，只分发特定类型的消息
      messageListeners.current.forEach(listener => {
        if (!listener.scene || listener.scene === data.scene) {
          listener(data, event);
        }
      }); // 调用所有注册的监听器
    } catch (e) {
      console.error('Failed to parse WebSocket message:', e, event.data); // 消息解析失败
      // // 对于非 JSON 格式的消息，可以直接传递原始数据或进行其他处理
      // messageListeners.current.forEach(listener => listener(event.data, event)); // 传递原始数据给监听器
    }
  }, []);

  const url = host && SESSID ? `wss://${host}/ws/user/event?_t=${SESSID}` : '';  // 使用 useWebSocket hook 来建立和管理 WebSocket 连接
  const { isConnected, sendMessage } = useWebSocket({
    url, // WebSocket 服务器 URL
    onMessage: handleGlobalMessage, // 将全局消息处理函数作为 onMessage 回调
    // onOpen: () => console.log('Global WebSocket connected via context!'), // 连接打开时的日志
    // onClose: (event) => console.log('Global WebSocket disconnected via context!', event.code, event.reason), // 连接关闭时的日志
    // onError: (event) => console.error('Global WebSocket error via context:', event), // 连接错误时的日志
  });

  // 构造 context 值，即使没有连接
  const contextValue = {
    isConnected: !!url && isConnected, // 只有 url 存在时才可能有连接
    sendMessage: url ? sendMessage : () => {
      console.warn('WebSocket 未初始化，无法发送消息');
    },
    addMessageListener,
  };
  // ✅ 始终提供 Provider，即使没有有效的 URL
  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * 用于在组件中访问全局 WebSocket 上下文的 Hook。
 * 如果不在 WebSocketProvider 内部使用，将抛出错误。
 */
export const useGlobalWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useGlobalWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

/**
 * 一个便捷的 Hook，用于订阅 WebSocket 消息。
 * 它会在组件挂载时注册回调函数，并在组件卸载时自动清理。
 */
export const useWebSocketMessage = (
  callback: WebSocketCallbackType,
  scene?: string,
) => {
  const { addMessageListener } = useGlobalWebSocket(); // 获取添加消息监听器的方法

  useEffect(() => {
    const cleanup = addMessageListener(callback, scene); // 注册消息监听器，并获取清理函数
    return () => cleanup(); // 组件卸载时执行清理函数，移除监听器
  }, [callback, addMessageListener, scene]); // 依赖项为 callback 和 addMessageListener，确保在它们变化时重新注册
}; 
